// Blockpalette der Welt. Der Index in BLOCKS ist die id, die in den Chunk-Bytearrays steht.
// Neue Blöcke kommen hinten dazu, damit bestehende Welten ihre ids behalten; die Palette ist
// auf 256 Einträge begrenzt, weil ein Chunk je Block ein Byte speichert.

export type BlockId = number; // 0 = Luft

/** Form für das Meshing: Würfel, Zaunsegment (schmal, verbindet sich) oder einzelner Pfosten. */
export type BlockShape = 'cube' | 'fence' | 'post';

/**
 * Kollisionshöhe von Zaun, Pfosten, Tor und Schild in Blöcken. Der Scheitel eines Sprungs liegt
 * bei rund 1,26 Blöcken; mit 1,5 kommt niemand zu Fuß über einen Zaun, fliegend schon
 * (docs/architektur-paket-2.md, Abschnitt „Welt mit Parzellen“). Das gilt nur vom Boden aus: Von
 * einem begehbaren Block daneben trüge der Sprung darüber, darum bleibt rund um jede Zaunlinie
 * ein Streifen frei (`ZAUN_FREIRAUM` in parcels.ts, ADR 0026).
 */
export const ZAUN_HOEHE = 1.5;

/** Die Einträge der Palette in der Reihenfolge ihrer ids; `luft` steht bei 0. */
const BLOCK_DEFS = [
  { name: 'luft', solid: false, shape: 'cube' },
  { name: 'gras', solid: true, shape: 'cube' },
  { name: 'erde', solid: true, shape: 'cube' },
  { name: 'stein', solid: true, shape: 'cube' },
  { name: 'pflaster', solid: true, shape: 'cube' },
  { name: 'balken', solid: true, shape: 'cube' },
  { name: 'putz', solid: true, shape: 'cube' },
  { name: 'dachziegel', solid: true, shape: 'cube' },
  { name: 'holz', solid: true, shape: 'cube' },
  { name: 'laub', solid: true, shape: 'cube' },
  { name: 'stamm', solid: true, shape: 'cube' },
  { name: 'zaun', solid: true, shape: 'fence', collisionHeight: ZAUN_HOEHE },
  { name: 'pfosten', solid: true, shape: 'post', collisionHeight: ZAUN_HOEHE },
  { name: 'wasser', solid: false, shape: 'cube' }, // man kann hineinlaufen
  { name: 'stroh', solid: true, shape: 'cube' },
  { name: 'fenster', solid: true, shape: 'cube' },
  { name: 'tuer', solid: false, shape: 'cube' }, // offene Tür: begehbar
  // Ab hier Paket 2 (docs/architektur-paket-2.md, „Welt mit Parzellen“); hinten angehängt, damit
  // die ids aus Paket 1 gleich bleiben.
  { name: 'tor', solid: true, shape: 'fence', collisionHeight: ZAUN_HOEHE }, // geschlossenes Tor
  { name: 'tor_offen', solid: false, shape: 'fence' }, // offenes Tor: Durchgang, wird nicht gezeichnet
  // Das Schild steht in der Zaunlinie neben dem Tor und ist so hoch wie sie: Sonst wäre es die
  // Stufe, über die man in den fremden Hof käme (ADR 0026). Den Namen malt der Client darüber.
  { name: 'schild', solid: true, shape: 'post', collisionHeight: ZAUN_HOEHE },
  { name: 'kiste', solid: true, shape: 'cube' },
  { name: 'acker', solid: true, shape: 'cube' }, // gepflügte Erde, Oberfläche des Feldes
  { name: 'apfel', solid: false, shape: 'cube' }, // hängt in der Krone, man greift hindurch
] as const satisfies readonly {
  name: string;
  solid: boolean;
  shape: BlockShape;
  collisionHeight?: number;
}[];

/** Alle Blocknamen der Palette, einschließlich `luft`. */
export type BlockName = (typeof BLOCK_DEFS)[number]['name'];

export interface BlockDef {
  id: BlockId;
  name: BlockName;
  solid: boolean;
  shape: BlockShape;
  /** Höhe des Kollisionsquaders in Blöcken; 1 für alles außer Zaun, Pfosten und Tor. */
  collisionHeight: number;
}

/** Palette; Index = id. */
export const BLOCKS: readonly BlockDef[] = BLOCK_DEFS.map((def, id) => ({
  id,
  collisionHeight: 1,
  ...def,
}));

/** Name → id, z. B. `BLOCK.gras`. */
export const BLOCK: Record<BlockName, BlockId> = Object.fromEntries(
  BLOCKS.map((block) => [block.name, block.id]),
) as Record<BlockName, BlockId>;

// BLOCK_DEFS ist ein Tupel, darum ist BLOCK_DEFS[0] für TypeScript sicher vorhanden.
const LUFT: BlockDef = { id: 0, collisionHeight: 1, ...BLOCK_DEFS[0] };

/** Der Paletteneintrag zu `id`; unbekannte ids gelten als Luft. */
export function blockDef(id: BlockId): BlockDef {
  return BLOCKS[id] ?? LUFT;
}

/**
 * Höhe des Kollisionsquaders eines Blocks in Blöcken. Zaun, Pfosten, Tor und Schild ragen über
 * ihre Zelle hinaus, damit man nicht über sie springt; alles andere ist genau einen Block hoch.
 * `moveAabb` nimmt diese Höhe für die Zelle unter dem Quader mit (ADR 0022).
 */
export function collisionHeight(id: BlockId): number {
  return blockDef(id).collisionHeight;
}
